// src/pages/DashboardPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardPage.css';

/* ─── helpers ─────────────────────────────────────────────── */
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
});
const authJsonHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
});

const STATUS_MAP = {
  baru:     { label: 'Masuk',    cls: 'badge-baru' },
  masuk:    { label: 'Masuk',    cls: 'badge-baru' },
  diproses: { label: 'Diproses', cls: 'badge-diproses' },
  selesai:  { label: 'Selesai',  cls: 'badge-selesai' },
  ditolak:  { label: 'Ditolak',  cls: 'badge-ditolak' },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
const today = () =>
  new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

function getFileType(nama) {
  const ext = nama.split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['zip','rar'].includes(ext)) return 'zip';
  if (['doc','docx'].includes(ext)) return 'word';
  if (['xls','xlsx'].includes(ext)) return 'excel';
  return 'file';
}
const FILE_EMOJI = { image:'🖼️', pdf:'📄', zip:'🗜️', word:'📝', excel:'📊', file:'📎' };

/* ─── Lampiran sub-components ─────────────────────────────── */
function LampiranItemReadOnly({ f }) {
  const tipe = getFileType(f.nama);
  const [imgError, setImgError] = useState(false);
  return (
    <li className="db-lampiran-item">
      {tipe === 'image' && !imgError
        ? <img src={f.url} alt={f.nama} className="db-lampiran-thumb" onError={() => setImgError(true)} />
        : <span className="db-lampiran-emoji">{FILE_EMOJI[tipe]}</span>}
      <span className="db-lampiran-nama">{f.nama}</span>
      <div className="db-lampiran-actions">
        <a href={f.url} target="_blank" rel="noreferrer" className="db-lampiran-btn" title="Buka">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
        </a>
        <a href={f.url} download={f.nama} className="db-lampiran-btn" title="Download">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </a>
      </div>
    </li>
  );
}

function LampiranAdminItem({ f, onDelete }) {
  const tipe = getFileType(f.nama);
  const [imgError, setImgError] = useState(false);
  return (
    <li className="db-lampiran-item">
      {tipe === 'image' && !imgError
        ? <img src={f.url} alt={f.nama} className="db-lampiran-thumb" onError={() => setImgError(true)} />
        : <span className="db-lampiran-emoji">{FILE_EMOJI[tipe]}</span>}
      <span className="db-lampiran-nama">{f.nama}</span>
      <div className="db-lampiran-actions">
        <a href={f.url} target="_blank" rel="noreferrer" className="db-lampiran-btn" title="Buka">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
        </a>
        <a href={f.url} download={f.nama} className="db-lampiran-btn" title="Download">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </a>
        <button className="db-lampiran-btn db-lampiran-del" onClick={() => onDelete(f.nama)} title="Hapus">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
        </button>
      </div>
    </li>
  );
}

/* ─── Sidebar nav ─────────────────────────────────────────── */
const NAV = [
  { key: 'dashboard', label: 'Dashboard',      icon: '🏠' },
  { key: 'pengaduan', label: 'Semua Pengaduan', icon: '📋' },
  { key: 'users',     label: 'Manajemen User',  icon: '👤', superOnly: true },
];

const ALLOWED_EXTENSIONS = ['.jpg','.jpeg','.png','.gif','.webp','.pdf','.zip','.rar','.doc','.docx','.xls','.xlsx','.txt'];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILES = 5;

/* ─── User form initial state ─────────────────────────────── */
const USER_FORM_INIT = { nama_lengkap: '', username: '', email: '', password: '', role: 'admin', status: 'aktif' };

/* ═══════════════════════════════════════════════════════════ */
export default function DashboardPage() {
  const navigate      = useNavigate();
  const fileUploadRef = useRef(null);

  const [user,        setUser]       = useState(null);
  const [stats,       setStats]      = useState(null);
  const [pengaduan,   setPengaduan]  = useState([]);
  const [users,       setUsers]      = useState([]);
  const [activePage,  setActivePage] = useState('dashboard');
  const [loading,     setLoading]    = useState(true);
  const [sidebarOpen, setSidebar]    = useState(false);
  const [toast,       setToast]      = useState('');

  /* pengaduan modal */
  const [selected,    setSelected]   = useState(null);
  const [editStatus,  setEditStatus] = useState('');
  const [editNote,    setEditNote]   = useState('');
  const [saving,      setSaving]     = useState(false);

  /* admin upload */
  const [adminFiles,       setAdminFiles]       = useState([]);
  const [uploadingAdmin,   setUploadingAdmin]   = useState(false);
  const [adminUploadError, setAdminUploadError] = useState('');
  const [adminDragOver,    setAdminDragOver]    = useState(false);

  /* ── USER CRUD state ─────────────────────────────────────── */
  const [userModal,     setUserModal]     = useState(null);   // null | 'create' | 'edit' | 'view'
  const [userTarget,    setUserTarget]    = useState(null);   // user object being edited/viewed
  const [userForm,      setUserForm]      = useState(USER_FORM_INIT);
  const [userFormErr,   setUserFormErr]   = useState('');
  const [userSaving,    setUserSaving]    = useState(false);
  const [showPassword,  setShowPassword]  = useState(false);  // toggle password visibility
  const [confirmDelete, setConfirmDelete] = useState(null);   // user to confirm delete

  /* ── auth guard ──────────────────────────────────────────── */
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored || !localStorage.getItem('token')) { navigate('/login'); return; }
    setUser(JSON.parse(stored));
  }, [navigate]);

  /* ── fetchers ────────────────────────────────────────────── */
  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/stats/', { headers: authJsonHeaders() });
      if (r.status === 401) { navigate('/login'); return; }
      setStats(await r.json());
    } catch {}
  }, [navigate]);

  const fetchPengaduan = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/pengaduan/', { headers: authJsonHeaders() });
      if (r.status === 401) { navigate('/login'); return; }
      const d = await r.json();
      setPengaduan(d.pengaduan || []);
    } catch {}
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/users/', { headers: authJsonHeaders() });
      if (r.status === 401 || r.status === 403) return;
      const d = await r.json();
      setUsers(d.users || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchPengaduan()]);
      if (user.role === 'super_admin') await fetchUsers();
      setLoading(false);
    })();
  }, [user, fetchStats, fetchPengaduan, fetchUsers]);

  useEffect(() => {
    if (!user) return;
    if (activePage === 'dashboard') fetchStats();
    if (activePage === 'pengaduan') fetchPengaduan();
    if (activePage === 'users' && user.role === 'super_admin') fetchUsers();
  }, [activePage, user, fetchStats, fetchPengaduan, fetchUsers]);

  /* ── pengaduan modal ─────────────────────────────────────── */
  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await fetch(`/api/admin/pengaduan/${selected.id}/`, {
        method: 'PUT', headers: authJsonHeaders(),
        body: JSON.stringify({ status: editStatus, catatan_admin: editNote }),
      });
      const d = await r.json();
      if (r.ok) { showToast('✓ Pengaduan berhasil diperbarui'); setSelected(d.pengaduan); await Promise.all([fetchStats(), fetchPengaduan()]); }
      else showToast(d.message || 'Gagal menyimpan');
    } catch { showToast('Gagal terhubung ke server'); }
    setSaving(false);
  };

  /* ── admin file upload ───────────────────────────────────── */
  const validateAdminFiles = (newFiles) => {
    const errors = [], valid = [];
    for (const f of newFiles) {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) { errors.push(`"${f.name}" tipe tidak didukung.`); continue; }
      if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) { errors.push(`"${f.name}" melebihi ${MAX_FILE_SIZE_MB} MB.`); continue; }
      valid.push(f);
    }
    setAdminFiles(prev => {
      const combined = [...prev, ...valid];
      if (combined.length > MAX_FILES) { errors.push(`Maksimal ${MAX_FILES} file.`); return combined.slice(0, MAX_FILES); }
      return combined;
    });
    setAdminUploadError(errors.length ? errors.join(' ') : '');
  };

  const handleUploadAdmin = async () => {
    if (!adminFiles.length || !selected) return;
    setUploadingAdmin(true); setAdminUploadError('');
    try {
      const fd = new FormData();
      adminFiles.forEach(f => fd.append('lampiran_admin', f));
      const r = await fetch(`/api/admin/pengaduan/${selected.id}/upload/`, { method: 'POST', headers: authHeaders(), body: fd });
      const d = await r.json();
      if (r.ok) { showToast(`✓ ${adminFiles.length} file berhasil diunggah`); setAdminFiles([]); setSelected(d.pengaduan); fetchPengaduan(); }
      else setAdminUploadError(d.message || 'Gagal upload.');
    } catch { setAdminUploadError('Gagal terhubung ke server.'); }
    setUploadingAdmin(false);
  };

  const handleDeleteAdminFile = async (nama) => {
    if (!selected || !window.confirm(`Hapus file "${nama}"?`)) return;
    try {
      const r = await fetch(`/api/admin/pengaduan/${selected.id}/upload/hapus/`, {
        method: 'DELETE', headers: authJsonHeaders(), body: JSON.stringify({ nama }),
      });
      const d = await r.json();
      if (r.ok) { showToast(`✓ File "${nama}" dihapus`); setSelected(prev => ({ ...prev, lampiran_admin: d.lampiran_admin })); fetchPengaduan(); }
      else showToast(d.message || 'Gagal hapus file');
    } catch { showToast('Gagal terhubung ke server'); }
  };

  const openDetail = (p) => {
    setSelected(p);
    setEditStatus(p.status === 'masuk' ? 'baru' : p.status);
    setEditNote(p.catatan_admin || '');
    setAdminFiles([]); setAdminUploadError('');
  };

  /* ══════════════════════════════════════════════════════════
     USER CRUD handlers
  ══════════════════════════════════════════════════════════ */

  const openCreateUser = () => {
    setUserForm(USER_FORM_INIT);
    setUserFormErr(''); setShowPassword(false);
    setUserTarget(null); setUserModal('create');
  };

  const openEditUser = (u) => {
    setUserForm({ nama_lengkap: u.nama_lengkap, username: u.username, email: u.email, password: '', role: u.role, status: u.status });
    setUserFormErr(''); setShowPassword(false);
    setUserTarget(u); setUserModal('edit');
  };

  const openViewUser = (u) => {
    setUserTarget(u); setShowPassword(false); setUserModal('view');
  };

  const closeUserModal = () => { setUserModal(null); setUserTarget(null); setUserFormErr(''); };

  /* CREATE */
  const handleCreateUser = async () => {
    const { nama_lengkap, username, email, password, role } = userForm;
    if (!nama_lengkap.trim() || !username.trim() || !email.trim() || !password.trim()) {
      setUserFormErr('Semua field wajib diisi.'); return;
    }
    setUserSaving(true); setUserFormErr('');
    try {
      const r = await fetch('/api/admin/users/', {
        method: 'POST', headers: authJsonHeaders(),
        body: JSON.stringify(userForm),
      });
      const d = await r.json();
      if (r.ok) { showToast('✓ User berhasil ditambahkan'); closeUserModal(); fetchUsers(); }
      else setUserFormErr(d.message || 'Gagal membuat user.');
    } catch { setUserFormErr('Gagal terhubung ke server.'); }
    setUserSaving(false);
  };

  /* EDIT */
  const handleEditUser = async () => {
    if (!userTarget) return;
    setUserSaving(true); setUserFormErr('');
    // Hanya kirim password jika diisi
    const payload = { ...userForm };
    if (!payload.password.trim()) delete payload.password;
    try {
      const r = await fetch(`/api/admin/users/${userTarget.id}/`, {
        method: 'PUT', headers: authJsonHeaders(),
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok) { showToast('✓ User berhasil diperbarui'); closeUserModal(); fetchUsers(); }
      else setUserFormErr(d.message || 'Gagal memperbarui user.');
    } catch { setUserFormErr('Gagal terhubung ke server.'); }
    setUserSaving(false);
  };

  /* DELETE */
  const handleDeleteUser = async (u) => {
    setConfirmDelete(null);
    try {
      const r = await fetch(`/api/admin/users/${u.id}/`, { method: 'DELETE', headers: authJsonHeaders() });
      const d = await r.json();
      if (r.ok) { showToast('✓ User berhasil dihapus'); fetchUsers(); }
      else showToast(d.message || 'Gagal menghapus user.');
    } catch { showToast('Gagal terhubung ke server'); }
  };

  /* TOGGLE STATUS */
  const handleToggleUser = async (u) => {
    const newStatus = u.status === 'aktif' ? 'nonaktif' : 'aktif';
    try {
      await fetch(`/api/admin/users/${u.id}/`, { method: 'PUT', headers: authJsonHeaders(), body: JSON.stringify({ status: newStatus }) });
      fetchUsers(); showToast(`✓ User ${newStatus}`);
    } catch { showToast('Gagal'); }
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleLogout = async () => {
    await fetch('/api/logout/', { method: 'POST', headers: authJsonHeaders() });
    localStorage.clear(); navigate('/login');
  };

  if (!user) return null;

  const STAT_CARDS = [
    { label: 'Total Laporan',   value: stats?.total    ?? 0, icon: '📋', tag: 'Semua',      tagCls: 'tag-blue' },
    { label: 'Laporan Masuk',   value: stats?.baru     ?? 0, icon: '📥', tag: 'Baru',       tagCls: 'tag-orange' },
    { label: 'Sedang Diproses', value: stats?.diproses ?? 0, icon: '🔄', tag: 'Aktif',      tagCls: 'tag-yellow' },
    { label: 'Selesai',         value: stats?.selesai  ?? 0, icon: '✅', tag: 'Tertangani', tagCls: 'tag-green' },
    { label: 'Ditolak',         value: stats?.ditolak  ?? 0, icon: '❌', tag: 'Ditinjau',   tagCls: 'tag-red' },
  ];

  return (
    <div className="db-root">
      {sidebarOpen && <div className="db-overlay" onClick={() => setSidebar(false)} />}

      {/* ════ SIDEBAR ════ */}
      <aside className={`db-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="db-sidebar-logo">
          <img src="/logolemhannas.png" alt="Logo" className="db-logo-img" />
          <div className="db-logo-text">
            <span className="db-logo-title">Admin Panel</span>
            <span className="db-logo-sub">Lemhannas RI</span>
          </div>
        </div>
        <nav className="db-nav">
          {NAV.filter(n => !n.superOnly || user.role === 'super_admin').map(n => (
            <button key={n.key} className={`db-nav-item ${activePage === n.key ? 'active' : ''}`}
              onClick={() => { setActivePage(n.key); setSidebar(false); }}>
              <span className="db-nav-icon">{n.icon}</span>
              <span className="db-nav-label">{n.label}</span>
            </button>
          ))}
        </nav>
        <button className="db-logout-btn" onClick={handleLogout}><span>🚪</span> Keluar</button>
      </aside>

      {/* ════ MAIN ════ */}
      <main className="db-main">
        <header className="db-topbar">
          <button className="db-hamburger" onClick={() => setSidebar(true)}><span /><span /><span /></button>
          <div className="db-topbar-title">{NAV.find(n => n.key === activePage)?.label || 'Dashboard'}</div>
          <div className="db-topbar-user">
            <div className="db-avatar">{user.nama_lengkap?.[0] || 'A'}</div>
            <div className="db-user-info">
              <span className="db-user-name">{user.nama_lengkap}</span>
              <span className="db-user-role">{user.role.replace('_', ' ')}</span>
            </div>
          </div>
        </header>

        <div className="db-content">
          {loading ? (
            <div className="db-loading"><div className="db-spinner" /><p>Memuat data…</p></div>
          ) : (
            <>
              {/* DASHBOARD */}
              {activePage === 'dashboard' && (
                <div className="db-fade">
                  <div className="db-page-header">
                    <h1 className="db-page-title">Dashboard</h1>
                    <p className="db-page-sub">{today()} — Ringkasan laporan pengaduan</p>
                  </div>
                  <div className="db-stats-grid">
                    {STAT_CARDS.map((s, i) => (
                      <div className="db-stat-card" key={i} style={{ animationDelay: `${i * 0.07}s` }}>
                        <div className="db-stat-top"><span className="db-stat-icon">{s.icon}</span><span className={`db-stat-tag ${s.tagCls}`}>{s.tag}</span></div>
                        <div className="db-stat-value">{s.value}</div>
                        <div className="db-stat-label">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="db-card">
                    <div className="db-card-header">
                      <h2 className="db-card-title">Laporan Terbaru</h2>
                      <button className="db-link-btn" onClick={() => setActivePage('pengaduan')}>Lihat semua →</button>
                    </div>
                    <PengaduanTable rows={pengaduan.slice(0, 5)} onDetail={openDetail} />
                  </div>
                </div>
              )}

              {/* PENGADUAN */}
              {activePage === 'pengaduan' && (
                <div className="db-fade">
                  <div className="db-page-header">
                    <h1 className="db-page-title">Semua Pengaduan</h1>
                    <p className="db-page-sub">Total {pengaduan.length} laporan masuk</p>
                  </div>
                  <div className="db-card"><PengaduanTable rows={pengaduan} onDetail={openDetail} /></div>
                </div>
              )}

              {/* ════ USERS PAGE ════ */}
              {activePage === 'users' && user.role === 'super_admin' && (
                <div className="db-fade">
                  <div className="db-page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <h1 className="db-page-title">Manajemen User</h1>
                      <p className="db-page-sub">{users.length} user terdaftar</p>
                    </div>
                    <button className="db-btn-add-user" onClick={openCreateUser}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Tambah User
                    </button>
                  </div>
                  <div className="db-card">
                    <div className="db-table-wrap">
                      <table className="db-table">
                        <thead>
                          <tr><th>Nama</th><th>Username</th><th>Email</th><th>Role</th><th>Status</th><th>Aksi</th></tr>
                        </thead>
                        <tbody>
                          {users.map(u => (
                            <tr key={u.id}>
                              <td>{u.nama_lengkap}</td>
                              <td><code>{u.username}</code></td>
                              <td>{u.email}</td>
                              <td><span className="db-role-badge">{u.role.replace('_',' ')}</span></td>
                              <td><span className={`db-status-dot ${u.status === 'aktif' ? 'dot-green' : 'dot-red'}`}>● {u.status}</span></td>
                              <td>
                                <div className="db-user-actions">
                                  {/* Lihat detail */}
                                  <button className="db-user-btn btn-view" title="Lihat detail" onClick={() => openViewUser(u)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                  </button>
                                  {/* Edit */}
                                  <button className="db-user-btn btn-edit" title="Edit" onClick={() => openEditUser(u)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  </button>
                                  {/* Toggle aktif/nonaktif */}
                                  {u.role !== 'super_admin' && (
                                    <button className={`db-user-btn ${u.status === 'aktif' ? 'btn-toggle-off' : 'btn-toggle-on'}`}
                                      title={u.status === 'aktif' ? 'Nonaktifkan' : 'Aktifkan'}
                                      onClick={() => handleToggleUser(u)}>
                                      {u.status === 'aktif'
                                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="16" cy="12" r="3" fill="currentColor"/></svg>
                                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="8" cy="12" r="3" fill="currentColor"/></svg>}
                                    </button>
                                  )}
                                  {/* Delete */}
                                  {u.role !== 'super_admin' && (
                                    <button className="db-user-btn btn-delete" title="Hapus" onClick={() => setConfirmDelete(u)}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* ════ PENGADUAN MODAL ════ */}
      {selected && (
        <div className="db-modal-backdrop" onClick={() => setSelected(null)}>
          <div className="db-modal" onClick={e => e.stopPropagation()}>
            <div className="db-modal-header">
              <div>
                <span className="db-modal-kode">{selected.kode_unik}</span>
                <h2 className="db-modal-title">{selected.judul}</h2>
              </div>
              <button className="db-modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="db-modal-body">
              <div className="db-modal-row"><span className="db-modal-lbl">Pelapor</span><span>{selected.nama_pelapor || 'Anonim'}</span></div>
              {selected.email_pelapor && <div className="db-modal-row"><span className="db-modal-lbl">Email</span><span>{selected.email_pelapor}</span></div>}
              <div className="db-modal-row"><span className="db-modal-lbl">Tanggal</span><span>{fmtDate(selected.created_at)}</span></div>
              <div className="db-modal-desc"><span className="db-modal-lbl">Deskripsi</span><p>{selected.deskripsi}</p></div>

              {selected.lampiran?.length > 0 && (
                <div className="db-lampiran-box">
                  <div className="db-lampiran-header">
                    <span className="db-modal-lbl">📎 Lampiran Pelapor</span>
                    <span className="db-lampiran-count">{selected.lampiran.length} file</span>
                  </div>
                  <ul className="db-lampiran-list">{selected.lampiran.map((f, i) => <LampiranItemReadOnly key={i} f={f} />)}</ul>
                </div>
              )}

              <div className="db-lampiran-box db-lampiran-admin-box">
                <div className="db-lampiran-header">
                  <span className="db-modal-lbl">📤 Lampiran Balasan Admin</span>
                  {selected.lampiran_admin?.length > 0 && <span className="db-lampiran-count">{selected.lampiran_admin.length} file</span>}
                </div>
                {selected.lampiran_admin?.length > 0
                  ? <ul className="db-lampiran-list">{selected.lampiran_admin.map((f, i) => <LampiranAdminItem key={i} f={f} onDelete={handleDeleteAdminFile} />)}</ul>
                  : <p className="db-lampiran-empty">Belum ada lampiran balasan.</p>}

                {(selected.lampiran_admin?.length || 0) < MAX_FILES && (
                  <>
                    <div className={`db-admin-dropzone ${adminDragOver ? 'dragover' : ''}`}
                      onClick={() => fileUploadRef.current?.click()}
                      onDragOver={e => { e.preventDefault(); setAdminDragOver(true); }}
                      onDragLeave={() => setAdminDragOver(false)}
                      onDrop={e => { e.preventDefault(); setAdminDragOver(false); validateAdminFiles(Array.from(e.dataTransfer.files)); }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display:'block', flexShrink:0 }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <span className="db-admin-dropzone-label">Klik atau seret file balasan</span>
                      <span className="db-admin-dropzone-hint">JPG · PNG · PDF · ZIP · DOC · XLS · TXT · Maks. {MAX_FILE_SIZE_MB} MB</span>
                    </div>
                    <input ref={fileUploadRef} type="file" multiple accept={ALLOWED_EXTENSIONS.join(',')}
                      style={{ display: 'none' }} onChange={e => { validateAdminFiles(Array.from(e.target.files)); e.target.value=''; }} />
                  </>
                )}

                {adminFiles.length > 0 && (
                  <ul className="db-lampiran-list" style={{ marginTop: 8 }}>
                    {adminFiles.map((f, i) => (
                      <li key={i} className="db-lampiran-item db-lampiran-staged">
                        <span className="db-lampiran-emoji">{FILE_EMOJI[getFileType(f.name)]}</span>
                        <div className="db-lampiran-info">
                          <span className="db-lampiran-nama">{f.name}</span>
                          <span className="db-lampiran-size">{f.size < 1048576 ? (f.size/1024).toFixed(1)+' KB' : (f.size/1048576).toFixed(1)+' MB'}</span>
                        </div>
                        <button className="db-lampiran-btn db-lampiran-del" onClick={() => setAdminFiles(p => p.filter((_,j) => j!==i))} title="Hapus">✕</button>
                      </li>
                    ))}
                  </ul>
                )}
                {adminUploadError && <p className="db-admin-upload-error">⚠️ {adminUploadError}</p>}
                {adminFiles.length > 0 && (
                  <button className="db-btn-upload-admin" onClick={handleUploadAdmin} disabled={uploadingAdmin}>
                    {uploadingAdmin ? <><span className="db-mini-spin" /> Mengunggah…</> : `⬆ Upload ${adminFiles.length} File`}
                  </button>
                )}
              </div>

              <div className="db-modal-field">
                <label className="db-modal-lbl">Update Status</label>
                <select className="db-select" value={editStatus} onChange={e => setEditStatus(e.target.value)}>
                  <option value="baru">Masuk</option>
                  <option value="diproses">Sedang Diproses</option>
                  <option value="selesai">Selesai</option>
                  <option value="ditolak">Ditolak</option>
                </select>
              </div>
              <div className="db-modal-field">
                <label className="db-modal-lbl">Catatan Admin</label>
                <textarea className="db-textarea" rows={3} placeholder="Tulis catatan penanganan…" value={editNote} onChange={e => setEditNote(e.target.value)} />
              </div>
            </div>
            <div className="db-modal-footer">
              <button className="db-btn-cancel" onClick={() => setSelected(null)}>Batal</button>
              <button className="db-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? <span className="db-mini-spin" /> : 'Simpan Perubahan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════ USER MODAL: VIEW ════ */}
      {userModal === 'view' && userTarget && (
        <div className="db-modal-backdrop" onClick={closeUserModal}>
          <div className="db-modal db-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="db-modal-header">
              <h2 className="db-modal-title">Detail User</h2>
              <button className="db-modal-close" onClick={closeUserModal}>✕</button>
            </div>
            <div className="db-modal-body">
              {/* Avatar */}
              <div className="db-user-avatar-wrap">
                <div className="db-user-avatar-lg">{userTarget.nama_lengkap?.[0]?.toUpperCase()}</div>
                <div>
                  <p className="db-user-detail-name">{userTarget.nama_lengkap}</p>
                  <span className={`db-role-badge`}>{userTarget.role.replace('_',' ')}</span>
                </div>
              </div>

              <div className="db-user-detail-grid">
                <div className="db-user-detail-item"><span className="db-modal-lbl">Username</span><code>{userTarget.username}</code></div>
                <div className="db-user-detail-item"><span className="db-modal-lbl">Email</span><span>{userTarget.email}</span></div>
                <div className="db-user-detail-item">
                  <span className="db-modal-lbl">Password</span>
                  <div className="db-password-row">
                    <span className="db-password-val">{showPassword ? userTarget.password : '••••••••'}</span>
                    <button className="db-password-toggle" onClick={() => setShowPassword(p => !p)} title={showPassword ? 'Sembunyikan' : 'Tampilkan'}>
                      {showPassword
                        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                    </button>
                  </div>
                </div>
                <div className="db-user-detail-item">
                  <span className="db-modal-lbl">Status</span>
                  <span className={`db-status-dot ${userTarget.status === 'aktif' ? 'dot-green' : 'dot-red'}`}>● {userTarget.status}</span>
                </div>
              </div>
            </div>
            <div className="db-modal-footer">
              <button className="db-btn-cancel" onClick={closeUserModal}>Tutup</button>
              <button className="db-btn-save" style={{ background: 'linear-gradient(135deg,#2f54eb,#5b8dee)' }} onClick={() => { closeUserModal(); openEditUser(userTarget); }}>Edit User</button>
            </div>
          </div>
        </div>
      )}

      {/* ════ USER MODAL: CREATE / EDIT ════ */}
      {(userModal === 'create' || userModal === 'edit') && (
        <div className="db-modal-backdrop" onClick={closeUserModal}>
          <div className="db-modal db-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="db-modal-header">
              <h2 className="db-modal-title">{userModal === 'create' ? '➕ Tambah User Baru' : '✏️ Edit User'}</h2>
              <button className="db-modal-close" onClick={closeUserModal}>✕</button>
            </div>
            <div className="db-modal-body">
              {userFormErr && <div className="db-form-error">⚠️ {userFormErr}</div>}

              <div className="db-modal-field">
                <label className="db-modal-lbl">Nama Lengkap *</label>
                <input className="db-input" placeholder="Nama lengkap" value={userForm.nama_lengkap}
                  onChange={e => setUserForm(p => ({ ...p, nama_lengkap: e.target.value }))} />
              </div>
              <div className="db-modal-field">
                <label className="db-modal-lbl">Username *</label>
                <input className="db-input" placeholder="username" value={userForm.username}
                  onChange={e => setUserForm(p => ({ ...p, username: e.target.value }))}
                  disabled={userModal === 'edit'} style={userModal === 'edit' ? { opacity: 0.6, cursor: 'not-allowed' } : {}} />
                {userModal === 'edit' && <p className="db-input-hint">Username tidak dapat diubah.</p>}
              </div>
              <div className="db-modal-field">
                <label className="db-modal-lbl">Email *</label>
                <input className="db-input" type="email" placeholder="email@domain.com" value={userForm.email}
                  onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="db-modal-field">
                <label className="db-modal-lbl">{userModal === 'edit' ? 'Password Baru (kosongkan jika tidak diubah)' : 'Password *'}</label>
                <div className="db-password-input-wrap">
                  <input className="db-input" type={showPassword ? 'text' : 'password'}
                    placeholder={userModal === 'edit' ? 'Biarkan kosong jika tidak diubah' : 'Password'}
                    value={userForm.password}
                    onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))} />
                  <button className="db-password-eye" type="button" onClick={() => setShowPassword(p => !p)} title={showPassword ? 'Sembunyikan' : 'Tampilkan'}>
                    {showPassword
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                  </button>
                </div>
              </div>
              <div className="db-form-row-2">
                <div className="db-modal-field">
                  <label className="db-modal-lbl">Role *</label>
                  <select className="db-select" value={userForm.role} onChange={e => setUserForm(p => ({ ...p, role: e.target.value }))}>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <div className="db-modal-field">
                  <label className="db-modal-lbl">Status *</label>
                  <select className="db-select" value={userForm.status} onChange={e => setUserForm(p => ({ ...p, status: e.target.value }))}>
                    <option value="aktif">Aktif</option>
                    <option value="nonaktif">Nonaktif</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="db-modal-footer">
              <button className="db-btn-cancel" onClick={closeUserModal}>Batal</button>
              <button className="db-btn-save" onClick={userModal === 'create' ? handleCreateUser : handleEditUser} disabled={userSaving}>
                {userSaving ? <span className="db-mini-spin" /> : userModal === 'create' ? 'Tambah User' : 'Simpan Perubahan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════ CONFIRM DELETE ════ */}
      {confirmDelete && (
        <div className="db-modal-backdrop" onClick={() => setConfirmDelete(null)}>
          <div className="db-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="db-confirm-icon">🗑️</div>
            <h3 className="db-confirm-title">Hapus User?</h3>
            <p className="db-confirm-text">
              Akun <strong>{confirmDelete.nama_lengkap}</strong> ({confirmDelete.username}) akan dihapus permanen dan tidak dapat dikembalikan.
            </p>
            <div className="db-confirm-actions">
              <button className="db-btn-cancel" onClick={() => setConfirmDelete(null)}>Batal</button>
              <button className="db-btn-danger" onClick={() => handleDeleteUser(confirmDelete)}>Ya, Hapus</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="db-toast">{toast}</div>}
    </div>
  );
}

/* ─── Pengaduan Table ─────────────────────────────────────── */
function PengaduanTable({ rows, onDetail }) {
  if (!rows.length) return <p className="db-empty">Belum ada laporan masuk.</p>;
  return (
    <div className="db-table-wrap">
      <table className="db-table">
        <thead><tr><th>Kode</th><th>Judul</th><th>Pelapor</th><th>Status</th><th>Tanggal</th><th>Aksi</th></tr></thead>
        <tbody>
          {rows.map(p => {
            const s = STATUS_MAP[p.status] || { label: p.status, cls: '' };
            return (
              <tr key={p.id}>
                <td><span className="db-kode">{p.kode_unik}</span></td>
                <td className="db-judul-cell">{p.judul}</td>
                <td className="db-judul-cell">{p.nama_pelapor || 'Anonim'}</td>
                <td><span className={`db-badge ${s.cls}`}>● {s.label}</span></td>
                <td className="db-date-cell">{new Date(p.created_at).toLocaleDateString('id-ID',{day:'2-digit',month:'short',year:'numeric'})}</td>
                <td><button className="db-detail-btn" onClick={() => onDetail(p)}>Detail</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}